// #68: the Excel import's «القسم» / «المجموعة» columns place each imported
// beneficiary in an EXISTING group of the organization. Names that do not
// match are reported back, never created.
//
// Model (virtual-membership.ts): a section is a supportGroups doc with
// groupType 'parent'; its groups point to it with parentGroupId. Sections
// have no membership docs of their own (assertMembershipAllowed refuses
// them), so a beneficiary is always placed in a group, and the section
// column only narrows which group a name refers to.
import { normalizeForSearch } from '@/lib/customers/customerDirectory';

export interface OrgGroup {
  id: string;
  name: string;
  groupType?: string;
  parentGroupId?: string | null;
}

export type GroupAssignment =
  | { kind: 'none' }
  | { kind: 'group'; groupId: string; groupName: string; sectionName?: string }
  | { kind: 'unknown-section'; sectionName: string }
  | { kind: 'unknown-group'; groupName: string; sectionName?: string }
  | { kind: 'ambiguous-group'; groupName: string }
  | { kind: 'section-needs-group'; sectionName: string };

const same = (a: string, b: string) => normalizeForSearch(a) === normalizeForSearch(b);
const isSection = (g: OrgGroup) => g.groupType === 'parent';

export function resolveImportGroup(groups: OrgGroup[], sectionName: string, groupName: string): GroupAssignment {
  const sectionCell = (sectionName || '').trim();
  const groupCell = (groupName || '').trim();
  if (!sectionCell && !groupCell) return { kind: 'none' };

  let section: OrgGroup | undefined;
  if (sectionCell) {
    section = groups.find((g) => isSection(g) && same(g.name, sectionCell));
    if (!section) return { kind: 'unknown-section', sectionName: sectionCell };
    if (!groupCell) return { kind: 'section-needs-group', sectionName: section.name };
  }

  const candidates = groups.filter(
    (g) => !isSection(g) && same(g.name, groupCell) && (!section || g.parentGroupId === section.id),
  );
  if (candidates.length === 0) {
    return { kind: 'unknown-group', groupName: groupCell, ...(section ? { sectionName: section.name } : {}) };
  }
  if (candidates.length > 1) return { kind: 'ambiguous-group', groupName: groupCell };

  const [group] = candidates;
  const parent = section ?? groups.find((g) => g.id === group.parentGroupId);
  return { kind: 'group', groupId: group.id, groupName: group.name, ...(parent ? { sectionName: parent.name } : {}) };
}

/** A short Arabic/English reason for the preview and the import summary. */
export function describeAssignment(a: GroupAssignment, rtl: boolean): string {
  switch (a.kind) {
    case 'none':
      return '—';
    case 'group':
      return a.sectionName ? `${a.sectionName} / ${a.groupName}` : a.groupName;
    case 'unknown-section':
      return rtl ? `قسم غير موجود: «${a.sectionName}»` : `Unknown section: "${a.sectionName}"`;
    case 'unknown-group':
      return a.sectionName
        ? (rtl ? `لا توجد مجموعة «${a.groupName}» في قسم «${a.sectionName}»` : `No group "${a.groupName}" in section "${a.sectionName}"`)
        : (rtl ? `مجموعة غير موجودة: «${a.groupName}»` : `Unknown group: "${a.groupName}"`);
    case 'ambiguous-group':
      return rtl ? `اسم المجموعة «${a.groupName}» مكرر في أكثر من قسم — أضف القسم` : `Group "${a.groupName}" exists in several sections — add the section`;
    case 'section-needs-group':
      return rtl ? `حدد المجموعة داخل قسم «${a.sectionName}»` : `Pick a group inside section "${a.sectionName}"`;
  }
}

/** Flatten getGroupHierarchy() output (sections with children) into one list. */
export function flattenHierarchy(tree: (OrgGroup & { children?: OrgGroup[] })[]): OrgGroup[] {
  const out: OrgGroup[] = [];
  const walk = (nodes: (OrgGroup & { children?: OrgGroup[] })[]) => {
    for (const n of nodes) {
      out.push({ id: n.id, name: n.name, groupType: n.groupType, parentGroupId: n.parentGroupId ?? null });
      if (n.children?.length) walk(n.children);
    }
  };
  walk(tree);
  return out;
}
